import React, { useEffect, useState } from 'react';
import { fetchGroupClients, getReportFirmId } from '../ClientSelection';
import './FirmPicker.css';

/**
 * Firm dropdown for the screens whose data is per firm but which are not the
 * customer reports: the company list and the new-product list.
 *
 * AllCompanies and NewProducts take a wClientId, so the firm has to be chosen
 * rather than assumed - the group id is not a firm.
 *
 * Unlike FirmSelect this keeps the choice in the page only: it does not write
 * to the stored `report-firm`, so browsing companies here cannot change which
 * firm a customer's ledger or pending bills are showing.
 *
 * onReady(firm) fires once the list has loaded and an initial firm is picked -
 * the list arrives after mount, so a screen that fetches on load needs this
 * rather than doing it itself.
 * onChange(firm) fires on every switch.
 */
const FirmPicker = ({ onReady, onChange, label = 'Firm', className = '' }) => {
    const [clients, setClients] = useState([]);
    const [selected, setSelected] = useState('');
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;

        fetchGroupClients()
            .then((list) => {
                if (cancelled) return;
                setClients(list);

                // Open on the firm the customer is already looking at
                // elsewhere when there is one, otherwise the first in the
                // group.
                const stored = getReportFirmId();
                const initial =
                    list.find((c) => c.wClientId === stored) || list[0];

                if (initial) {
                    setSelected(initial.wClientId);
                    if (onReady) onReady(initial);
                } else if (onReady) {
                    onReady(null);
                }
            })
            .catch((err) => {
                console.error('Could not load the firm list', err);
                if (cancelled) return;
                setFailed(true);
                if (onReady) onReady(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeHandler = (e) => {
        const id = e.target.value;
        const client = clients.find((c) => c.wClientId === id);
        if (!client) return;
        setSelected(id);
        if (onChange) onChange(client);
    };

    return (
        <div className={'firmPicker ' + className}>
            <label className='firmPickerLabel' htmlFor='firmPicker'>
                {label}
            </label>
            <select
                id='firmPicker'
                className='form-control firmPickerSelect'
                value={selected}
                onChange={changeHandler}
                disabled={loading || failed || clients.length === 0}
            >
                <option value=''>
                    {loading
                        ? 'Loading firms...'
                        : failed
                            ? 'Could not load firms'
                            : clients.length === 0
                                ? 'No firms available'
                                : 'Select firm'}
                </option>
                {clients.map((c) => (
                    <option key={c.wClientId} value={c.wClientId}>
                        {c.firmName}
                    </option>
                ))}
            </select>
        </div>
    );
};

export default FirmPicker;
